import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { clientFormSchema, type ClientFormData } from '../types'
import { useBranchOffices } from '../hooks/useBranchOffices'
import { usePermissions } from '@/modules/auth/hooks/usePermissions'
import { useFormFormatter } from '@/shared/hooks/useFormFormatter'

interface ClientFormProps {
  defaultValues?: Partial<ClientFormData>
  onSubmit: (data: ClientFormData) => void
  isSubmitting: boolean
  submitLabel?: string
  error?: string | null
}

export function ClientForm({
  defaultValues,
  onSubmit,
  isSubmitting,
  submitLabel = 'Guardar',
  error,
}: ClientFormProps) {
  const { branches, isLoading: branchesLoading } = useBranchOffices()
  const { isAdmin } = usePermissions()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      name: '',
      last_name: '',
      rut: '',
      phone_number: '',
      gender: '',
      commune: '',
      birthdate: '',
      email: '',
      client_test: false,
      ...defaultValues,
    },
  })

  // When branches finish loading, re-apply the default id_branch_office
  // so the <select> reflects the saved value (it may have been empty during load)
  useEffect(() => {
    if (!branchesLoading && defaultValues?.id_branch_office) {
      setValue('id_branch_office', defaultValues.id_branch_office)
    }
  }, [branchesLoading, defaultValues?.id_branch_office, setValue])

  const formatterFields = useMemo(() => [
    { name: 'rut' as const, formatter: 'rut' as const },
    { name: 'name' as const, formatter: 'name' as const },
    { name: 'last_name' as const, formatter: 'name' as const },
    { name: 'email' as const, formatter: 'email' as const },
    { name: 'phone_number' as const, formatter: 'phone' as const, trigger: 'blur' as const },
  ], [])

  const { registerWithFormatter } = useFormFormatter<ClientFormData>(
    watch, setValue, register, formatterFields,
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-2xl">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
          <input {...register('name')} className="input" />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
          <input {...register('last_name')} className="input" />
          {errors.last_name && (
            <p className="text-red-500 text-sm mt-1">{errors.last_name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">RUT *</label>
          <input {...register('rut')} className="input" placeholder="12345678-9" />
          {errors.rut && <p className="text-red-500 text-sm mt-1">{errors.rut.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input {...register('email')} type="email" className="input" />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
          <input
            {...registerWithFormatter('phone_number')}
            className="input"
            placeholder="+56 9 1234 5678"
            type="tel"
          />
          {errors.phone_number && (
            <p className="text-red-500 text-sm mt-1">{errors.phone_number.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Comuna</label>
          <input {...register('commune')} className="input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Género</label>
          <select {...register('gender')} className="input">
            <option value="">Seleccionar</option>
            <option value="Masculino">Masculino</option>
            <option value="Femenino">Femenino</option>
            <option value="Otro">Otro</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de nacimiento</label>
          <input {...register('birthdate')} type="date" className="input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal *</label>
          <select
            {...register('id_branch_office', { valueAsNumber: true })}
            className="input"
            disabled={branchesLoading}
          >
            <option value="">Seleccionar sucursal</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.commune} - {branch.address}
              </option>
            ))}
          </select>
          {errors.id_branch_office && (
            <p className="text-red-500 text-sm mt-1">{errors.id_branch_office.message}</p>
          )}
        </div>

        {isAdmin && (
          <div className="flex items-center gap-2 pt-6">
            <input
              {...register('client_test')}
              type="checkbox"
              id="client_test"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="client_test" className="text-sm text-gray-700">
              Cliente de prueba
            </label>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button type="submit" disabled={isSubmitting} className="btn btn-primary">
          {isSubmitting ? 'Guardando...' : submitLabel}
        </button>
      </div>
    </form>
  )
}
